import { apiFetch } from './api';

const API_URL = import.meta.env.VITE_API_URL || '';

/**
 * Anotação avulsa — o caderno do usuário.
 *
 * NÃO confundir com `DetalheConversa` (`OpportunityService`), que é a anotação
 * presa à oportunidade e alimenta o "parada há N dias" do Painel Operacional.
 * São objetos diferentes de propósito: aquela é a linha do tempo do negócio
 * (2 em cada 3 são escritas pelo sistema), esta é texto que a pessoa escreve.
 */
export interface Anotacao {
    anotacaoId: number;
    usuarioId: number;
    nomeUsuarioCriador: string | null;
    titulo: string | null;
    conteudo: string;
    /** Texto puro da prévia, gravado pela API — o cartão não limpa HTML. */
    resumo: string | null;
    fixada: boolean;
    etiquetas: string | null;
    compartilhada: boolean;
    dataCriacao: string;
    dataAtualizacao: string;
    /** Quando EU abri esta anotação pela última vez. Nulo = nunca abri. */
    ultimaVisita: string | null;
}

export interface ApiResponse<T> {
    sucesso: boolean;
    mensagem: string;
    dados: T;
}

export interface SalvarAnotacaoDTO {
    titulo: string | null;
    conteudo: string;
    fixada: boolean;
    etiquetas: string | null;
    compartilhada: boolean;
}

const json = { 'Content-Type': 'application/json' };

export const AnotacaoService = {
    getAll: async (): Promise<ApiResponse<Anotacao[]>> => {
        const response = await apiFetch(`${API_URL}/api/Anotacao/BuscarTodas`, { method: 'GET', headers: json });
        if (!response.ok) {
            /* Lista vazia não é erro de tela: a API devolve 400 quando não acha nada
               em vários controllers, e a tela precisa mostrar o estado vazio. */
            if (response.status === 400 || response.status === 404) {
                return { sucesso: true, mensagem: 'Sem anotações', dados: [] };
            }
            throw new Error('Erro ao buscar anotações');
        }
        return response.json();
    },

    getById: async (id: number): Promise<ApiResponse<Anotacao>> => {
        const response = await apiFetch(`${API_URL}/api/Anotacao/BuscarPorId/${id}`, { method: 'GET', headers: json });
        return response.json();
    },

    create: async (dto: SalvarAnotacaoDTO): Promise<ApiResponse<Anotacao>> => {
        const response = await apiFetch(`${API_URL}/api/Anotacao/Criar`, {
            method: 'POST',
            headers: json,
            body: JSON.stringify(dto),
        });
        return response.json();
    },

    update: async (id: number, dto: SalvarAnotacaoDTO): Promise<ApiResponse<Anotacao>> => {
        const response = await apiFetch(`${API_URL}/api/Anotacao/Atualizar/${id}`, {
            method: 'PUT',
            headers: json,
            body: JSON.stringify(dto),
        });
        return response.json();
    },

    /**
     * Marca "abri esta anotação agora" — alimenta a coluna "Última visita".
     * Falha em silêncio de propósito: visita é acessório, e não pode impedir
     * ninguém de ler a anotação.
     */
    registrarVisita: async (id: number): Promise<void> => {
        try {
            await apiFetch(`${API_URL}/api/Anotacao/RegistrarVisita/${id}`, { method: 'POST', headers: json });
        } catch {
            /* silêncio proposital */
        }
    },

    remove: async (id: number): Promise<ApiResponse<unknown>> => {
        const response = await apiFetch(`${API_URL}/api/Anotacao/Remover/${id}`, { method: 'DELETE', headers: json });
        return response.json();
    },

    /**
     * Sobe uma imagem do corpo e devolve a URL para pôr no `src`. `anotacaoId`
     * vai nulo enquanto a anotação não tem id — a API deixa a imagem solta e a
     * vincula no salvamento seguinte.
     */
    uploadImagem: async (arquivo: File, anotacaoId: number | null): Promise<string> => {
        const form = new FormData();
        form.append('Arquivo', arquivo);
        if (anotacaoId) form.append('AnotacaoId', String(anotacaoId));

        const response = await apiFetch(`${API_URL}/api/Anotacao/Imagens/Adicionar`, {
            method: 'POST',
            body: form,
        });
        const resultado: ApiResponse<{ imagemId: number }> = await response.json();
        if (!resultado.sucesso) throw new Error(resultado.mensagem || 'Erro ao enviar a imagem');

        return `${API_URL}/api/Anotacao/Imagens/${resultado.dados.imagemId}/Arquivo`;
    },
};

/**
 * Id do usuário logado, como número.
 *
 * Duas armadilhas medidas no `localStorage` real (11/09/2026), as duas já
 * tendo custado um bug: a chave é **`id`** (não `usuarioId` nem `idUsuario`)
 * e o valor é **string** (`"2"`). Comparar direto com o `usuarioId` da API,
 * que é número, dá `2 !== "2"` — foi o que fez a anotação do próprio dono
 * abrir em "somente leitura".
 */
export const usuarioLogadoId = (): number | null => {
    try {
        const bruto = JSON.parse(localStorage.getItem('user') || '{}')?.id;
        const numero = Number(bruto);
        return Number.isFinite(numero) ? numero : null;
    } catch {
        return null;
    }
};

/** A anotação é de outra pessoa (compartilhada comigo) — leitura, não escrita. */
export const ehDeOutroUsuario = (a: Anotacao): boolean => {
    const meuId = usuarioLogadoId();
    return meuId !== null && a.usuarioId !== meuId;
};

/** "Sem título" na lista é pior que herdar a primeira linha do corpo. */
export const tituloDaAnotacao = (a: Anotacao): string => {
    if (a.titulo && a.titulo.trim()) return a.titulo.trim();
    const primeiraLinha = (a.resumo || '').split(/[.\n]/)[0].trim();
    return primeiraLinha || 'Sem título';
};

export const etiquetasDaAnotacao = (a: Anotacao): string[] =>
    (a.etiquetas || '')
        .split(',')
        .map(e => e.trim())
        .filter(Boolean);
